/**
 * GraphErrorBanner — uniform error surface for any Microsoft
 * Graph error returned by the api. Reads the `code` field that
 * classifyGraphError sets on the response body and renders an
 * appropriately-styled card with a title + actionable advice.
 *
 * Used by every dialog that calls a Graph-backed endpoint
 * (CalendarEventPicker, FollowupSuggestionDialog) so users get
 * a consistent experience for the same failure modes regardless
 * of which surface they're on.
 *
 * Color coding:
 *   - reauth / scope_blocked  → terra-cotta (action required)
 *   - throttled / outage      → amber (wait and retry)
 *   - everything else         → red (general failure)
 */
import { ApiError } from '../lib/apiClient.ts';

export type GraphErrorCode =
  | 'reauth'
  | 'scope_blocked'
  | 'throttled'
  | 'upstream_outage'
  | 'not_found'
  | 'validation'
  | 'network'
  | 'unknown';

export interface ClassifiedGraphErrorBody {
  error?: string;
  code?: GraphErrorCode;
  title?: string;
  advice?: string;
  retryAfterSeconds?: number;
}

/** Pull the classified shape out of an ApiError, falling back
 *  to a generic body when the response wasn't classified
 *  (e.g. an old endpoint not yet wired through classifyGraphError
 *  on the server side). */
export function readGraphErrorBody(err: unknown): ClassifiedGraphErrorBody | null {
  if (!(err instanceof ApiError)) return null;
  if (typeof err.data !== 'object' || err.data === null) {
    return { error: err.message };
  }
  return err.data as ClassifiedGraphErrorBody;
}

interface BannerProps {
  err: unknown;
  /** Override / supplement the category-derived advice with
   *  context-specific guidance ("Sign out + back in to refresh
   *  calendar access" vs the generic "Sign out and sign back
   *  in"). Optional. */
  contextAdvice?: string;
}

export function GraphErrorBanner({ err, contextAdvice }: BannerProps) {
  const body = readGraphErrorBody(err);
  if (!body) return null;
  const code = body.code ?? 'unknown';
  const title = body.title ?? body.error ?? 'Microsoft request failed.';
  const advice = contextAdvice ?? body.advice;

  // Color tier — terra-cotta for "user must act," amber for
  // "wait and retry," red for "real failure."
  const tone =
    code === 'reauth' || code === 'scope_blocked'
      ? {
          border: 'border-terra-cotta/40',
          bg: 'bg-terra-cotta/10',
          text: 'text-terra-cotta',
          body: 'text-charcoal',
        }
      : code === 'throttled' || code === 'upstream_outage'
        ? {
            border: 'border-amber-300',
            bg: 'bg-amber-50',
            text: 'text-amber-900',
            body: 'text-amber-900/85',
          }
        : {
            border: 'border-red-200',
            bg: 'bg-red-50',
            text: 'text-red-700',
            body: 'text-red-700/85',
          };

  return (
    <div
      role="alert"
      className={`rounded-lg border ${tone.border} ${tone.bg} px-3 py-2 text-xs`}
    >
      <div className={`font-semibold ${tone.text}`}>{title}</div>
      {advice && <p className={`mt-0.5 ${tone.body}`}>{advice}</p>}
      {code === 'throttled' && body.retryAfterSeconds && (
        <p className={`mt-0.5 italic ${tone.body}`}>
          Microsoft suggested waiting {body.retryAfterSeconds} second
          {body.retryAfterSeconds === 1 ? '' : 's'}.
        </p>
      )}
    </div>
  );
}
